import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { RouterModule } from '@angular/router';
import { SvgUnifyDirective } from '@app/app/shared/directives/svg-unify-directive';
import { SgInputComponent } from '@app/app/shared/styleguide/components';
import { ButtonComponent } from '@app/app/shared/styleguide/components/buttons/default/button.component';
import { NavItemBaseComponent } from '@app/app/shared/styleguide/components/buttons/nav-item-base/nav-item-base.component';
import { IconComponent } from '@app/app/shared/styleguide/components/icon/icon.component';
import { SgTextareaComponent } from '@app/app/shared/styleguide/components/textarea/sg-textarea/sg-textarea.component';
import { StyleguideDemoComponent } from '@app/app/shared/styleguide/examples/styleguide-demo.component';

import { SidebarItemBaseComponent } from '@app/app/shared/styleguide/components/buttons/sidebar-item-base/sidebar-item-base.component';
import { TranslocoModule } from '@ngneat/transloco';
import { SgAutocompleteSearchComponent } from './components/autocomplete-search/sg-autocomplete-search.component';
import { BadgeBaseComponent } from './components/badge/badge-base.component';
import { BadgeClickableComponent } from './components/badge/badge-clickable.component';
import { PtlBreadcrumbComponent } from './components/breadcrumb/ptl-breadcrumb.component';
import { PtlBusyOverlayComponent } from './components/busy-overlay/ptl-busy-overlay.component';
import { SgButtonComponent } from './components/buttons/sg-button/sg-button.component';
import { PtlCardComponent } from './components/card/ptl-card.component';
import { SgCheckboxComponent } from './components/checkboxes/sg-checkbox/sg-checkbox.component';
import { PtlColorSwatchComponent } from './components/color-swatch/ptl-color-swatch.component';
import { DateInputComponent } from './components/date-input/date-input.component';
import { DateTimePickerComponent } from './components/date-time-picker/date-time-picker.component';
import { PtlDropdownComponent } from './components/dropdown/ptl-dropdown.component';
import { FileDownloadCardComponent } from './components/file-download-card/file-download-card.component';
import { PtlInsightComponent } from './components/insight/ptl-insight.component';
import { LabelComponent } from './components/label/label.component';
import { PtlModalComponent } from './components/modal/ptl-modal.component';
import { OverlineComponent } from './components/overline/overline.component';
import { PtlPageEmptyComponent } from './components/page-empty/ptl-page-empty.component';
import { PaginationFooterComponent } from './components/pagination/pagination-footer.component';
import { PtlPillComponent } from './components/pill/ptl-pill.component';
import { PtlProgressBarComponent } from './components/progress-bar/ptl-progress-bar.component';
import { PtlProgressRingComponent } from './components/progress-ring/ptl-progress-ring.component';
import { SgRadioGroupComponent } from './components/radio/sg-radio-group/sg-radio-group.component';
import { RangeComponent } from './components/range/range.component';
import { SearchComponent } from './components/search/search.component';
import { SgSelectComponent } from './components/selects/sg-select/sg-select.component';
import { PtlTableCellDirective } from './components/table/ptl-table-cell.directive';
import { PtlTableHeaderDirective } from './components/table/ptl-table-header.directive';
import { PtlTableComponent } from './components/table/ptl-table.component';
import { PtlTabsComponent } from './components/tabs/ptl-tabs.component';
import { TimePickerComponent } from './components/time-picker/time-picker.component';
import { PtlToastComponent } from './components/toast/ptl-toast.component';
import { ToggleChipComponent } from './components/toggle-chip/toggle-chip.component';
import { SgToggleComponent } from './components/toggles/sg-toggle/sg-toggle.component';
import { PtlTooltipComponent } from './components/tooltip/ptl-tooltip.component';
import { UploadFileAreaComponent } from './components/upload-file-area/upload-file-area.component';
import { PtlViewToggleComponent } from './components/view-toggle/ptl-view-toggle.component';

const STYLEGUIDE_COMPONENTS = [
  StyleguideDemoComponent,
  SgButtonComponent,
  SgInputComponent,
  SgRadioGroupComponent,
  NavItemBaseComponent,
  UploadFileAreaComponent,
  BadgeBaseComponent,
  BadgeClickableComponent,
  PaginationFooterComponent,
  SgSelectComponent,
  SgCheckboxComponent,
  PtlTableComponent,
];

const STYLEGUIDE_DIRECTIVES = [SvgUnifyDirective, PtlTableCellDirective, PtlTableHeaderDirective];

@NgModule({
  declarations: [...STYLEGUIDE_COMPONENTS, ...STYLEGUIDE_DIRECTIVES],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    OverlayModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatRadioModule,
    MatChipsModule,
    MatCardModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslocoModule,
    IconComponent,
    ButtonComponent,
    SgTextareaComponent,
    SgToggleComponent,
    SidebarItemBaseComponent,
    MatLegacyAutocompleteModule,
    OverlineComponent,
    LabelComponent,
    ToggleChipComponent,
    SearchComponent,
    DateInputComponent,
    RangeComponent,
    PtlCardComponent,
    PtlPillComponent,
    PtlViewToggleComponent,
    PtlTabsComponent,
    PtlProgressBarComponent,
    PtlProgressRingComponent,
    PtlBreadcrumbComponent,
    PtlInsightComponent,
    PtlToastComponent,
    PtlTooltipComponent,
    PtlDropdownComponent,
    PtlModalComponent,
    PtlPageEmptyComponent,
    PtlBusyOverlayComponent,
    PtlColorSwatchComponent,
    TimePickerComponent,
    DateTimePickerComponent,
    FileDownloadCardComponent,
    SgAutocompleteSearchComponent,
  ],
  exports: [
    ...STYLEGUIDE_COMPONENTS,
    SgAutocompleteSearchComponent,
    ...STYLEGUIDE_DIRECTIVES,
    IconComponent,
    ButtonComponent,
    SgTextareaComponent,
    SgToggleComponent,
    OverlineComponent,
    LabelComponent,
    ToggleChipComponent,
    SearchComponent,
    DateInputComponent,
    RangeComponent,
    PtlCardComponent,
    PtlPillComponent,
    PtlViewToggleComponent,
    PtlTabsComponent,
    PtlProgressBarComponent,
    PtlProgressRingComponent,
    PtlBreadcrumbComponent,
    PtlInsightComponent,
    PtlToastComponent,
    PtlTooltipComponent,
    PtlDropdownComponent,
    PtlModalComponent,
    PtlPageEmptyComponent,
    PtlBusyOverlayComponent,
    PtlColorSwatchComponent,
    TimePickerComponent,
    DateTimePickerComponent,
    FileDownloadCardComponent,
  ],
})
export class StyleguideModule {}
